import React from "react";
import {
  Divider,
  Dialog,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckIcon from "@mui/icons-material/Check";
import * as S from "./style";
import { useLanguage } from "../../../contexts/LanguageContext";
import {
  InfiniteScrollList,
  useInfiniteScroll,
} from "../../../components/InfiniteScrollList";
import { getModelRunsByPatientId } from "../requests";

const PAGE_SIZE = 50;

interface DateOption {
  runId: string;
  date: Date;
}
 
/** Chat models the backend's RTX gRPC inference server has loaded. */
export const COPILOT_MODEL_MAPPING = {
  "pestle": "Pestle-27B"
}

export const COPILOT_MODEL_DESCRIPTION_KEYS = {
  "pestle": "copilot.model.pestle.description"
}

export const COPILOT_MODELS = Object.keys(COPILOT_MODEL_MAPPING);

export const DEFAULT_COPILOT_MODEL = "pestle";

interface AllRunsProps {
  /** First page of model runs (already loaded by the parent). */
  initialRuns: any[];
  /** Total number of model runs for this patient (from the first fetch). */
  totalRunCount: number;
  /** The patient whose runs we paginate. */
  patientId: string;
  selectedRunId: string | null;
  dateOptions: DateOption[];
  onSelectRun: (runId: string) => void;
  language: string;
  formatRunLabel: (date: Date, showTime: boolean) => string;
  /** Currently selected chat model (passed through to the live chat stream). */
  selectedModel: string;
  onSelectModel: (model: string) => void;
  /** Title of the currently open conversation, shown next to the vitals date dropdown. */
  conversationTitle?: string | null;
}

function AllRuns({
  initialRuns,
  totalRunCount,
  patientId,
  selectedRunId,
  dateOptions,
  onSelectRun,
  language,
  formatRunLabel,
  selectedModel,
  onSelectModel,
  conversationTitle,
}: AllRunsProps) {
  const { t } = useLanguage();
  const [open, setOpen] = React.useState(false);

  const [modelAnchor, setModelAnchor] = React.useState<null | HTMLElement>(null);
  const modelMenuOpen = Boolean(modelAnchor);
  const [runAnchor, setRunAnchor] = React.useState<null | HTMLElement>(null);
  const runMenuOpen = Boolean(runAnchor);
  // Model-choice picker hidden for now (Pestle is the only model). Flip to
  // true to restore; selectedModel still defaults to DEFAULT_COPILOT_MODEL.
  const SHOW_MODEL_PICKER = false;

  // Memoize the fetch callback so it doesn't change on every render
  const fetchPage = React.useCallback(
    async (offset: number, limit: number) => {
      const res = await getModelRunsByPatientId(patientId, limit, offset);
      return res.data;
    },
    [patientId]
  );

  const { items, hasMore, isLoading, loadMore, reset } = useInfiniteScroll({
    initialItems: initialRuns,
    totalCount: totalRunCount,
    pageSize: PAGE_SIZE,
    fetchPage,
  });

  // A previous conversation can reference an older run that isn't among the
  // quick date options (5 newest) or the preloaded pages (50 newest). When the
  // selected run can't be resolved locally, page through the remaining runs
  // until we find it so the dropdown can show its date instead of the
  // "Select date" placeholder.
  const [resolvedRun, setResolvedRun] = React.useState<any | null>(null);
  // Only locks in once a search actually concludes (found, or pages exhausted)
  // — NOT when it merely starts. Locking on start would let a search that gets
  // cancelled mid-flight (e.g. because `items` changes right after kicking off,
  // as it does on the very first load — see the sync effect below) permanently
  // block any retry, leaving the label stuck on "Select date".
  const notFoundForRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!selectedRunId) return;
    const inQuick = dateOptions.some((opt) => opt.runId === selectedRunId);
    const inItems = items.some((r) => r.model_run_id === selectedRunId);
    if (inQuick || inItems) return;
    if (resolvedRun?.model_run_id === selectedRunId) return;
    if (notFoundForRef.current === selectedRunId) return;
    let cancelled = false;
    (async () => {
      let offset = items.length;
      while (!cancelled) {
        let page: any[] = [];
        try {
          page = await fetchPage(offset, PAGE_SIZE);
        } catch {
          return;
        }
        if (cancelled) return;
        const found = page.find((r) => r.model_run_id === selectedRunId);
        offset += page.length;
        if (found) {
          setResolvedRun(found);
          return;
        }
        if (page.length < PAGE_SIZE) {
          notFoundForRef.current = selectedRunId; // exhausted all runs — not found
          return;
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedRunId, dateOptions, items, fetchPage, resolvedRun]);

  // Sync the hook whenever the parent's initial data changes (new patient, poll refresh, etc.)
  const prevInitialRef = React.useRef(initialRuns);
  const prevTotalRef = React.useRef(totalRunCount);
  React.useEffect(() => {
    if (
      prevInitialRef.current !== initialRuns ||
      prevTotalRef.current !== totalRunCount
    ) {
      prevInitialRef.current = initialRuns;
      prevTotalRef.current = totalRunCount;
      reset(initialRuns, totalRunCount);
    }
  }, [initialRuns, totalRunCount, reset]);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleSelect = (runId: string) => {
    setOpen(false);
    onSelectRun(runId);
  };

  const handleSelectQuickRun = (runId: string) => {
    setRunAnchor(null);
    if (runId !== selectedRunId) {
      onSelectRun(runId);
    }
  };

  // Label for the currently selected measurement day. Prefer the quick options
  // (last 5 days); fall back to the paginated run list when an older run was
  // selected through the "See more dates" dialog.
  const selectedRunLabel = React.useMemo(() => {
    const quick = dateOptions.find((opt) => opt.runId === selectedRunId);
    if (quick) return formatRunLabel(quick.date, true);
    const run =
      items.find((r) => r.model_run_id === selectedRunId) ??
      (resolvedRun?.model_run_id === selectedRunId ? resolvedRun : null);
    return run?.end_date ? formatRunLabel(new Date(run.end_date), true) : null;
  }, [dateOptions, selectedRunId, items, formatRunLabel, resolvedRun]);

  // dateOptions is oldest-first, so the last entry is the latest vital reading.
  const isLatestRun =
    dateOptions.length > 0 &&
    selectedRunId === dateOptions[dateOptions.length - 1].runId;

  if (initialRuns.length === 0 && dateOptions.length === 0) return null;

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ pb: 1, flexShrink: 0, gap: 2 }}>
        {SHOW_MODEL_PICKER && (
          <>
        <S.ModelSelectButton
          size="small"
          disableRipple
          variant="outlined"
          endIcon={<KeyboardArrowDownIcon sx={{ fontSize: "14px !important" }} />}
          onClick={(e) => setModelAnchor(e.currentTarget)}
        >
          <Typography variant="body2">
          {COPILOT_MODEL_MAPPING[selectedModel]}
          </Typography>
        </S.ModelSelectButton>
        <Menu
          anchorEl={modelAnchor}
          open={modelMenuOpen}
          onClose={() => setModelAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{ sx: { width: 350, borderRadius: "6px", mt: 0.5 } }}
          MenuListProps={{ sx: { py: 0 } }}
        >
          {COPILOT_MODELS.map((m) => (
            <MenuItem
              key={m}
              onClick={() => { onSelectModel(m); setModelAnchor(null); }}
              sx={{ gap: 1, alignItems: "flex-start", py: 1, whiteSpace: "normal" }}
            >
              <CheckIcon
                sx={{
                  fontSize: 14,
                  mt: "2px",
                  flexShrink: 0,
                  visibility: m === selectedModel ? "visible" : "hidden",
                }}
              />
              <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontSize: 13, whiteSpace: "normal", wordBreak: "break-word" }}>
                  {COPILOT_MODEL_MAPPING[m]}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11, lineHeight: 1.3, whiteSpace: "normal", wordBreak: "break-word" }}>
                  {t(COPILOT_MODEL_DESCRIPTION_KEYS[m])}
                </Typography>
              </Stack>
            </MenuItem>
          ))}
        </Menu>
          </>
        )}
        {initialRuns.length > 0 && (
          <S.VitalsSelectButton
            size="small"
            variant="outlined"
            aria-label={t('runs.vitalsAt')}
            endIcon={
              <KeyboardArrowDownIcon
                sx={{
                  fontSize: "14px !important",
                  transform: runMenuOpen ? "rotate(180deg)" : "none",
                  transition: "transform 0.15s ease",
                }}
              />
            }
            onClick={(e) => setRunAnchor(e.currentTarget)}
          >
            <Typography
              component="span"
              variant="caption"
              sx={{ fontSize: 11, fontWeight: 500, color: "text.secondary" }}
            >
              {t('runs.vitalsAt')}
            </Typography>
            <Typography component="span" variant="body2" sx={{ fontSize: 11, fontWeight: 600 }}>
              {selectedRunLabel ?? t('runs.selectDate')}
            </Typography>
            {isLatestRun && (
              <Typography
                component="span"
                variant="caption"
                sx={{ fontSize: 11, fontWeight: 500, color: "text.secondary" }}
              >
                ({t('runs.latest')})
              </Typography>
            )}
          </S.VitalsSelectButton>
        )}
        <Menu
          anchorEl={runAnchor}
          open={runMenuOpen}
          onClose={() => setRunAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{ sx: { borderRadius: "6px", mt: 0.5 } }}
          MenuListProps={{ sx: { py: 0 } }}
        >
          {[...dateOptions].reverse().map((opt) => {
            const isSelected = opt.runId === selectedRunId;
            return (
              <MenuItem
                key={opt.runId}
                onClick={() => handleSelectQuickRun(opt.runId)}
                sx={{ gap: 1, py: 0.75 }}
              >
                <CheckIcon
                  sx={{
                    fontSize: 14,
                    flexShrink: 0,
                    visibility: isSelected ? "visible" : "hidden",
                  }}
                />
                <Typography variant="body2" sx={{ fontSize: 12, whiteSpace: "nowrap" }}>
                  {formatRunLabel(opt.date, true)}
                </Typography>
              </MenuItem>
            );
          })}
          {initialRuns.length > dateOptions.length && <Divider sx={{ my: 0.5 }} />}
          {initialRuns.length > dateOptions.length && (
            <MenuItem
              onClick={() => {
                setRunAnchor(null);
                handleOpen();
              }}
              sx={{ gap: 1, py: 0.75 }}
            >
              <HistoryRoundedIcon sx={{ fontSize: 14, flexShrink: 0, color: "text.secondary" }} />
              <Typography variant="body2" sx={{ fontSize: 12, whiteSpace: "nowrap" }}>
                {t('runs.seeMoreDates')}
              </Typography>
            </MenuItem>
          )}
        </Menu>
        {conversationTitle && (
          <S.ConversationTitle variant="body2" title={conversationTitle}>
            {conversationTitle}
          </S.ConversationTitle>
        )}
      </Stack>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { overflow: "hidden", height: 500 } }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 700, fontSize: 15 }}>
          {t('runs.allModelRuns')}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ ml: 1, fontWeight: 400 }}
          >
            {totalRunCount} {t('runs.total')}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <InfiniteScrollList
            hasMore={hasMore}
            isLoading={isLoading}
            onLoadMore={loadMore}
            threshold={150}
            sx={{
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11, py: 0.75 }}>{t('runs.numberSymbol')}</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11, py: 0.75 }}>{t('runs.date')}</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11, py: 0.75 }}>{t('runs.tftModel')}</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11, py: 0.75 }}>{t('runs.copilotModel')}</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11, py: 0.75 }}>{t('runs.status')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((run, idx) => {
                  const isSelected = run.model_run_id === selectedRunId;
                  return (
                    <S.ClickableTableRow
                      key={run.model_run_id}
                      hover
                      selected={isSelected}
                      onClick={() => handleSelect(run.model_run_id)}
                    >
                      <TableCell sx={{ fontSize: 11, py: 0.5, color: "text.disabled" }}>
                        {idx + 1}
                      </TableCell>
                      <TableCell sx={{ fontSize: 11, py: 0.5, fontWeight: isSelected ? 600 : 400 }}>
                        {run.end_date
                          ? new Intl.DateTimeFormat(
                              { en: "en-US", fr: "fr-FR", no: "nb-NO" }[language] || "en-US",
                              {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              }
                            ).format(new Date(run.end_date))
                          : "—"}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: 11,
                          py: 0.5,
                          maxWidth: 160,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {run.tft_model || "—"}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: 11,
                          py: 0.5,
                          maxWidth: 160,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {run.copilot_model || "—"}
                      </TableCell>
                      <TableCell sx={{ py: 0.5 }}>
                        <S.StatusChip
                          label={run.status ?? "active"}
                          size="small"
                          isActive={run.status === "active"}
                        />
                      </TableCell>
                    </S.ClickableTableRow>
                  );
                })}
              </TableBody>
            </Table>
          </InfiniteScrollList>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default AllRuns;
